import { useRef, useEffect, useState, useCallback } from "react";

interface UseScrollSectionsResult {
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  activeSection: number;
  scrollToSection: (index: number) => void;
  setActiveSection: React.Dispatch<React.SetStateAction<number>>;
}

export const useScrollSections = (
  totalSections = 3,
  offsetHeight = 0
): UseScrollSectionsResult => {
  const scrollContainerRef = useRef<HTMLDivElement>(null!);
  const [activeSection, setActiveSection] = useState(0);
  const activeSectionRef = useRef(0);
  const isScrolling = useRef(false);
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null);
  const updateTimeout = useRef<NodeJS.Timeout | null>(null);

  const scrollToSection = useCallback((index: number) => {
    const scrollable = scrollContainerRef.current;
    if (!scrollable) return;

    const section = scrollable.querySelectorAll<HTMLElement>(".section")[index];
    if (!section) return;

    isScrolling.current = true;
    section.scrollIntoView({ behavior: "smooth", block: "start" });

    if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
    scrollTimeout.current = setTimeout(() => {
      isScrolling.current = false;
    }, 1000);

    // Delay active section update just for smoothness
    if (updateTimeout.current) clearTimeout(updateTimeout.current);
    updateTimeout.current = setTimeout(() => {
      activeSectionRef.current = index;
      setActiveSection(index);
    }, 250);
  }, []);

  // IntersectionObserver (immediate update when user scrolls manually)
  useEffect(() => {
    const container = scrollContainerRef.current;
    const sections = container?.querySelectorAll<HTMLElement>(".section");
    if (!container || !sections) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.find((entry) => entry.isIntersecting);
        if (!visible) return;

        const index = Array.from(sections).indexOf(
          visible.target as HTMLElement
        );

        if (
          index !== -1 &&
          !isScrolling.current &&
          activeSectionRef.current !== index
        ) {
          activeSectionRef.current = index;
          setActiveSection(index);
        }
      },
      {
        root: container,
        threshold: 0.6,
      }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  // Prevent scroll up on mobile when on first section
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    let startY = 0;

    const onTouchStart = (e: TouchEvent) => {
      startY = e.touches[0].clientY;
    };

    const onTouchMove = (e: TouchEvent) => {
      const currentY = e.touches[0].clientY;
      const deltaY = currentY - startY;

      if (deltaY > 0 && activeSectionRef.current === 0) {
        e.preventDefault();
      }
    };

    container.addEventListener("touchstart", onTouchStart, { passive: false });
    container.addEventListener("touchmove", onTouchMove, { passive: false });

    return () => {
      container.removeEventListener("touchstart", onTouchStart);
      container.removeEventListener("touchmove", onTouchMove);
    };
  }, []);

  // Mouse wheel scroll logic
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      if (isScrolling.current) return;

      const deltaY = e.deltaY;

      if (deltaY < 0 && activeSectionRef.current === 0) return;

      if (deltaY > 0 && activeSectionRef.current < totalSections - 1) {
        scrollToSection(activeSectionRef.current + 1);
      } else if (deltaY < 0 && activeSectionRef.current > 0) {
        scrollToSection(activeSectionRef.current - 1);
      }
    };

    container.addEventListener("wheel", handleWheel, { passive: false });
    return () => container.removeEventListener("wheel", handleWheel);
  }, [scrollToSection, totalSections]);

  return {
    scrollContainerRef,
    activeSection,
    scrollToSection,
    setActiveSection,
  };
};
